import { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './ForgotPassword.module.css';

import { useNavigate } from 'react-router';
import { NavigationType } from 'react-router-dom';
import { RouterState } from '@remix-run/router';
import router from '../../../Router';

import {
  useVerifyEmailMutation,
  useVerifyCodeMutation,
} from '../../../services/api';
import { ValidationResult } from '../../../types/login';

import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {
  AUTH_SENT,
  EMAIL,
  ENTER,
  ENTER_EMAIL_MESSAGE,
  ENTER_VERIFICATION_CODE,
  FIND,
  ID,
  NEXT,
  PASSWORD,
  PREV,
  REQUEST,
  CONFIRM,
  VERIFICATION_CODE,
  VERIFICATION_MSG,
} from '../../../constants';
import { checkEmailPattern } from '../../../utils/util';

export default function ForgotPassword() {
  const cx = classNames.bind(styles);
  const navigate = useNavigate();

  const forgotPwdSchema = yup.object({
    email: yup.string().required(),
    certPwd: yup.string().required(),
  });

  const { register, getValues, watch } = useForm({
    resolver: yupResolver(forgotPwdSchema),
    defaultValues: { email: '', certPwd: '' },
  });

  const [verifyEmail] = useVerifyEmailMutation();
  const [verifyCode] = useVerifyCodeMutation();
  const [verifiedEmailRes, setVerifiedEmailRes] = useState<ValidationResult>();
  const [verifiedCodeMsg, setVerifiedCodeMsg] = useState('');
  const [isResponseCode, setIsResponseCode] = useState(false);
  const [isRequestCode, setIsRequestCode] = useState(false);

  const requestCode = async () => {
    if (isRequestCode) return;
    setIsRequestCode(true);

    try {
      const res = await verifyEmail({
        email: getValues('email'),
      }).unwrap();
      setVerifiedEmailRes(res);
      res.message === 'OK' && setIsResponseCode(true);
    } catch (error) {
      console.log('Failed to request', error);
    }
  };

  const verifyResCode = async () => {
    try {
      const res = await verifyCode(getValues()).unwrap();
      setVerifiedCodeMsg(res.message);
    } catch (error) {
      console.log('Failed to verify', error);
    }
  };

  const goBack = (state: RouterState) => {
    if (
      state.historyAction === NavigationType.Pop &&
      state.location.pathname.includes('reset-password')
    ) {
      navigate('../forgot-password', { replace: true });
    }
  };

  useEffect(() => {
    router.subscribe(goBack);
    return router.subscribe(goBack);
  }, []);

  return (
    <>
      <h2>{`${PASSWORD} ${FIND}`}</h2>
      <div>
        <div className={cx('input_area')}>
          <div className={cx('input_box')}>
            <span className={cx('blind')}>{`${ID} ${ENTER}`}</span>
            <input
              type="email"
              title={`${EMAIL} ${ENTER}`}
              placeholder={ENTER_EMAIL_MESSAGE}
              autoComplete="username"
              autoCapitalize="none"
              {...register('email')}
            />
            {verifiedEmailRes?.message !== 'OK' && (
              <span className={cx('msg', 'invalid')}>
                {verifiedEmailRes?.message}
              </span>
            )}
          </div>

          <div className={cx('btn_wrap')}>
            <button
              type="button"
              className={cx('btn', 'b_t', 'dull', {
                'is--disabled':
                  !watch('email') || !checkEmailPattern(getValues('email')),
              })}
              onClick={requestCode}
            >
              <span>{`${VERIFICATION_CODE} ${REQUEST}`}</span>
            </button>

            {isResponseCode && <p className={cx('done_t')}>{AUTH_SENT}</p>}
          </div>

          {isResponseCode && (
            <>
              <div className={cx('input_box')}>
                <span
                  className={cx('blind')}
                >{`${VERIFICATION_CODE} ${ENTER}`}</span>
                <input
                  type="text"
                  title={`${VERIFICATION_CODE} ${ENTER}`}
                  placeholder={ENTER_VERIFICATION_CODE}
                  autoCapitalize="none"
                  {...register('certPwd')}
                />
                {verifiedCodeMsg !== 'OK' && (
                  <span className={cx('msg', 'invalid')}>
                    {verifiedCodeMsg}
                  </span>
                )}
              </div>
              <div className={cx('btn_wrap')}>
                {verifiedCodeMsg !== 'OK' && (
                  <button
                    type="button"
                    className={cx('btn', 'b_t', 'dull', {
                      'is--disabled': !watch('certPwd'),
                    })}
                    onClick={verifyResCode}
                  >
                    <span>{`${VERIFICATION_CODE} ${CONFIRM}`}</span>
                  </button>
                )}

                {verifiedCodeMsg === 'OK' && (
                  <p className={cx('done_t')}>{VERIFICATION_MSG}</p>
                )}
              </div>
            </>
          )}
        </div>

        <div className={cx('btn_wrap', 'bottom', 'cell')}>
          <button
            type="button"
            className={cx('btn', 'b_t', 'dull', 's44')}
            onClick={() => navigate(-1)}
          >
            <span>{PREV}</span>
          </button>
          <button
            type="button"
            className={cx('btn', 'b_t', 's44', {
              'is--disabled': verifiedCodeMsg !== 'OK',
            })}
            onClick={() =>
              navigate('../reset-password', {
                state: { email: getValues('email') },
              })
            }
          >
            <span>{NEXT}</span>
          </button>
        </div>
      </div>
    </>
  );
}
